var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useMemo } from 'react';
import { useServicesContext, useStockContext } from '../../context';
import { getProducts, getDefaultProduct } from '../api';
import { readFinance, saveCampaignInStore, saveFinanceInStore } from '../utils/sessionStorage';
import { resolveDisclaimer } from '../../components/finance/services/crs/helpers';
import { getFSAGDefaultProduct, getPaymentValue, mergeDeep, patchFormParameter, patchProductData, } from '../utils/crs-helper';
import { getActiveCampaign } from '../../components/finance/dynamic-finance/campaign-helper';
export const useDynamicFinancing = ({ vehicle }) => {
    var _a;
    const trackingService = useServicesContext().getAdditionalService('audi-tracking-service');
    const storeName = vehicle.basic.type.toUpperCase() === 'N' ? 'VTP_FINANCING_NC' : 'VTP_FINANCING_UC';
    const { svd } = useStockContext();
    const { getConfiguration, getEnvironmentConfig } = useServicesContext();
    const [campaign, setCampaign] = useState(null);
    const [financeData, setFinanceData] = useState(null);
    const [financeRequestData, setFinanceRequestData] = useState(vehicle.detail.financingRequest.Request);
    const [apiErrorInFinance, setApiErrorInFinance] = useState(false);
    const [rate, setRate] = useState('');
    const [calculationDisclaimer, setCalculationDisclaimer] = useState('');
    const [rateText, setRateText] = useState('');
    let productParameter = {};
    let formParameter = {};
    const scopesFromConfig = (_a = getConfiguration()) === null || _a === void 0 ? void 0 : _a.scopes;
    const financingDisclaimersTextStyle = scopesFromConfig === null || scopesFromConfig === void 0 ? void 0 : scopesFromConfig.interpretDisclaimersTextStyle;
    const currencyPattern = useMemo(() => { var _a; return (_a = getConfiguration()) === null || _a === void 0 ? void 0 : _a.currencyPattern; }, [getConfiguration]);
    const getPaymentDescription = (parameters) => {
        if (campaign &&
            campaign.filterText &&
            (!Object.keys(productParameter).length ||
                (productParameter && productParameter['@CampaignID']))) {
            return campaign.filterText;
        }
        if (typeof (parameters === null || parameters === void 0 ? void 0 : parameters.Description) === 'string') {
            return parameters.Description;
        }
        if (parameters === null || parameters === void 0 ? void 0 : parameters.Description['#text']) {
            return parameters.Description['#text'];
        }
        return '';
    };
    const useCampaignAsProduct = () => {
        if (campaign === null) {
            return;
        }
        if (Object.keys(campaign).length &&
            financeRequestData.Product &&
            campaign.campaignId &&
            campaign.typeDetail &&
            (!Object.keys(productParameter).length ||
                (productParameter && productParameter['@CampaignID']))) {
            financeRequestData.Product = {
                '@CampaignID': campaign.campaignId,
                '@ID': campaign.typeDetail,
            };
            setFinanceRequestData(financeRequestData);
            saveCampaignInStore(vehicle.basic.id, storeName, campaign);
        }
    };
    const loadFinanceData = () => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c, _d, _e;
        const endpointUrl = (_b = getEnvironmentConfig()) === null || _b === void 0 ? void 0 : _b.crsServiceBaseUrl;
        let defaultProduct = Array.isArray(financeRequestData.Product)
            ? financeRequestData.Product[0]
            : financeRequestData.Product;
        if ((_c = vehicle.detail.financingProductsRequest) === null || _c === void 0 ? void 0 : _c.Request) {
            const products = yield getProducts(endpointUrl, (_d = vehicle.detail.financingProductsRequest) === null || _d === void 0 ? void 0 : _d.Request, vehicle.basic.id, storeName, trackingService, false);
            defaultProduct = getFSAGDefaultProduct(products);
            patchProductData(products.Products.Product, financeRequestData, productParameter, defaultProduct);
            setFinanceRequestData(financeRequestData);
        }
        defaultProduct = patchFormParameter(defaultProduct, formParameter) || defaultProduct;
        const defaultResponse = yield getDefaultProduct(endpointUrl, mergeDeep(financeRequestData, { Product: defaultProduct }), vehicle.basic.id, storeName, trackingService, false);
        const financingStore = {
            defaultResponse: { Response: defaultResponse },
            defaultProductData: defaultProduct,
            vehicleData: {
                default: financeRequestData,
                product: (_e = vehicle.detail.financingProductsRequest) === null || _e === void 0 ? void 0 : _e.Request,
            },
        };
        saveFinanceInStore(vehicle.basic.id, storeName, financingStore);
        setFinanceData(defaultResponse);
    });
    const loadCampaigns = () => __awaiter(void 0, void 0, void 0, function* () {
        const activeCampaign = yield getActiveCampaign(vehicle, getEnvironmentConfig(), svd, getConfiguration());
        if (!activeCampaign || !Object.keys(activeCampaign).length) {
            saveCampaignInStore(vehicle.basic.id, storeName, {});
        }
        setCampaign(activeCampaign);
        useCampaignAsProduct();
    });
    const session = readFinance(storeName);
    useEffect(() => {
        readFinance(storeName).then((res) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            if (res && res.financing) {
                productParameter = (_a = res === null || res === void 0 ? void 0 : res.financing[vehicle.basic.id]) === null || _a === void 0 ? void 0 : _a.productParameter;
                formParameter = (_b = res === null || res === void 0 ? void 0 : res.financing[vehicle.basic.id]) === null || _b === void 0 ? void 0 : _b.formParameter;
                const defaultResponse = (_c = res.financing[vehicle.basic.id]) === null || _c === void 0 ? void 0 : _c.defaultResponse;
                if ((_d = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _d === void 0 ? void 0 : _d.Parameters) {
                    if ((_f = (_e = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _e === void 0 ? void 0 : _e.Parameters) === null || _f === void 0 ? void 0 : _f.Group) {
                        const group = (_h = (_g = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _g === void 0 ? void 0 : _g.Parameters) === null || _h === void 0 ? void 0 : _h.Group;
                        const newRate = Array.isArray(group)
                            ? getPaymentValue(group[0].Parameter, currencyPattern)
                            : getPaymentValue(group.Parameter, currencyPattern);
                        if (newRate !== rate) {
                            setRate(newRate);
                        }
                    }
                    const newRateText = getPaymentDescription((_j = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _j === void 0 ? void 0 : _j.Parameters);
                    if (newRateText !== rateText) {
                        setRateText(newRateText);
                    }
                    const newCalculationDisclaimer = resolveDisclaimer(defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response, 'CalculationDisclaimer', financingDisclaimersTextStyle);
                    if (newCalculationDisclaimer !== calculationDisclaimer) {
                        setCalculationDisclaimer(newCalculationDisclaimer);
                    }
                }
            }
        });
    }, [session]);
    useEffect(() => {
        const getFinanceData = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                yield loadFinanceData();
                setCalculationDisclaimer(resolveDisclaimer(financeData, 'CalculationDisclaimer', financingDisclaimersTextStyle) ||
                    '');
            }
            catch (_a) {
                setApiErrorInFinance(true);
            }
        });
        getFinanceData();
    }, []);
    useEffect(() => {
        var _a;
        if (financeData === null || financeData === void 0 ? void 0 : financeData.Parameters) {
            if ((_a = financeData === null || financeData === void 0 ? void 0 : financeData.Parameters) === null || _a === void 0 ? void 0 : _a.Group) {
                const group = financeData.Parameters.Group;
                const newRate = Array.isArray(group)
                    ? getPaymentValue(group[0].Parameter, currencyPattern)
                    : getPaymentValue(group.Parameter, currencyPattern);
                if (newRate !== rate) {
                    setRate(newRate);
                }
            }
            const newRateText = getPaymentDescription(financeData.Parameters);
            if (newRateText !== rateText) {
                setRateText(newRateText);
            }
            const newCalculationDisclaimer = resolveDisclaimer(financeData, 'CalculationDisclaimer', financingDisclaimersTextStyle);
            if (newCalculationDisclaimer !== calculationDisclaimer) {
                setCalculationDisclaimer(newCalculationDisclaimer);
            }
        }
    }, [financeData === null || financeData === void 0 ? void 0 : financeData.Parameters]);
    if (!campaign) {
        loadCampaigns();
    }
    if (!financeData && apiErrorInFinance) {
        return null;
    }
    return { rate, rateText, calculationDisclaimer };
};
